<template>
  <PortalPageShell
    :title="pageTitle"
    :subtitle="$t('portal.checkinManager')"
  >
    <div class="portal-form-card edit-checkin-row form-group">
      <div class="my-2">
        <div class="loader" v-if="loading"></div>
        <input
          class="form-control"
          type="text"
          id="code"
          placeholder="Check-in Row Code"
          v-model="row.code"
          maxlength="10"
        /><br />
        <select
          class="form-control"
          id="airline_code"
          v-model="row.airline_code"
        >
          <option value="">Select Airline (optional)</option>
          <option
            v-for="airline in airlines"
            :key="airline.id"
            :value="airline.code"
          >
            {{ airline.name }} ({{ airline.code }})
          </option>
        </select><br />

        <!-- Airline Check-in Logos (only when an airline is selected) -->
        <div v-if="selectedAirline" class="logo-selection mb-3">
          <label class="d-block mb-2">Select Check-in Logo (optional)</label>
          <div class="row">
            <!-- New dedicated Check-in Logo -->
            <div
              class="col-md-4 mb-2"
              v-if="selectedAirline.logo_checkin"
            >
              <div
                class="card logo-option"
                :class="{ selected: row.logo_file === selectedAirline.logo_checkin }"
                @click="row.logo_file = selectedAirline.logo_checkin"
              >
                <div class="card-body text-center">
                  <div class="logo-preview-wrapper">
                    <img
                      :src="logoUrl(selectedAirline.logo_checkin)"
                      :key="selectedAirline.logo_checkin + '-' + logoCacheBust"
                      alt="Check-in logo"
                      class="checkin-logo-preview"
                    />
                  </div>
                  <small class="d-block mt-1">Check-in</small>
                </div>
              </div>
            </div>

            <div
              class="col-md-4 mb-2"
              v-if="selectedAirline.logo_checkin_economy"
            >
              <div
                class="card logo-option"
                :class="{ selected: row.logo_file === selectedAirline.logo_checkin_economy }"
                @click="row.logo_file = selectedAirline.logo_checkin_economy"
              >
                <div class="card-body text-center">
                  <div class="logo-preview-wrapper">
                    <img
                      :src="logoUrl(selectedAirline.logo_checkin_economy)"
                      :key="selectedAirline.logo_checkin_economy + '-' + logoCacheBust"
                      alt="Check-in Economy logo"
                      class="checkin-logo-preview"
                    />
                  </div>
                  <small class="d-block mt-1">Check-in Economy</small>
                </div>
              </div>
            </div>

            <div
              class="col-md-4 mb-2"
              v-if="selectedAirline.logo_checkin_premium"
            >
              <div
                class="card logo-option"
                :class="{ selected: row.logo_file === selectedAirline.logo_checkin_premium }"
                @click="row.logo_file = selectedAirline.logo_checkin_premium"
              >
                <div class="card-body text-center">
                  <div class="logo-preview-wrapper">
                    <img
                      :src="logoUrl(selectedAirline.logo_checkin_premium)"
                      :key="selectedAirline.logo_checkin_premium + '-' + logoCacheBust"
                      alt="Check-in Premium logo"
                      class="checkin-logo-preview"
                    />
                  </div>
                  <small class="d-block mt-1">Check-in Premium</small>
                </div>
              </div>
            </div>
          </div>
        </div>

        <textarea
          class="form-control"
          id="description"
          placeholder="Description (optional)"
          v-model="row.description"
          rows="3"
        ></textarea><br />
        <div v-if="$router.currentRoute.name === 'edit-checkin-row'" class="custom-control custom-switch">
          <input
            type="checkbox"
            class="custom-control-input"
            id="isActiveSwitch"
            v-model="row.is_active"
          />
          <label class="custom-control-label mt-4" for="isActiveSwitch">Is Active</label>
        </div>
      </div>
      <button
        v-if="$router.currentRoute.name === 'edit-checkin-row'"
        class="btn btn-primary mx-2"
        @click="updateRow($route.params.id)"
      >
        Update
      </button>
      <button
        v-if="$router.currentRoute.name === 'new-checkin-row'"
        class="btn btn-success mx-2"
        @click="addRow"
      >
        Save
      </button>
    </div>
  </PortalPageShell>
</template>

<script>
import PortalPageShell from '../layout/PortalPageShell.vue';
import axios from 'axios';
import { routeNames } from '../../js/routeNames';

export default {
  name: 'EditCheckinRow',
  components: {
    PortalPageShell,
  },
  data() {
    return {
      loading: false,
      row: {
        id: null,
        code: '',
        airline_code: '',
        logo_file: '',
        description: '',
        is_active: true,
      },
      airlines: [],
      nameCheck: '',
      logoCacheBust: Date.now(),
    };
  },
  mounted() {
    this.loadData();
  },
  computed: {
    routeNames: () => routeNames,
    pageTitle() {
      return this.$router.currentRoute.name === 'new-checkin-row'
        ? 'Add Check-in Row'
        : 'Edit Check-in Row';
    },
    selectedAirline() {
      if (!this.row.airline_code || !this.airlines || !this.airlines.length) {
        return null;
      }
      return this.airlines.find(
        (a) => a && a.code === this.row.airline_code
      ) || null;
    },
  },
  methods: {
    async loadData() {
      this.loading = true;
      try {
        // Load airlines first
        const airlinesResp = await axios.get(this.$hostName + '/airlines/details');
        this.airlines = (airlinesResp && airlinesResp.data) || [];
        
        // If editing, fetch the latest row data from API
        if (this.$router.currentRoute.name === 'edit-checkin-row') {
          const rowId = this.$route.params.id;
          if (rowId) {
            const rowResp = await axios.get(this.$hostName + '/checkin-row/' + rowId);
            if (rowResp && rowResp.data) {
              const data = rowResp.data;
              this.row = {
                id: data.id,
                code: data.code || '',
                airline_code: data.airline_code || '',
                description: data.description || '',
                logo_file: data.logo_file || '',
                is_active: data.is_active != null ? data.is_active : true,
              };
              this.nameCheck = data.code || '';
              // Force logo refresh
              this.logoCacheBust = Date.now();
            }
          }
        }
      } catch (error) {
        console.error('Failed to load data:', error);
        // Fallback to route params if API fails
        if (this.$router.currentRoute.name === 'edit-checkin-row') {
          this.row.id = this.$route.params.id;
          this.row.code = this.$route.params.code || '';
          this.row.airline_code = this.$route.params.airline_code || '';
          this.row.description = this.$route.params.description || '';
          this.row.logo_file = this.$route.params.logo_file || '';
          this.row.is_active = this.$route.params.is_active != null ? this.$route.params.is_active : true;
          this.nameCheck = this.$route.params.code || '';
        }
      } finally {
        this.loading = false;
      }
    },
    logoUrl(fileName) {
      if (!fileName) return '';
      const base = process.env.VUE_APP_AIRLINE_IMAGES_BASE || process.env.VUE_APP_IMAGES_BASE || '';
      if (!base) return fileName;
      const trimmed = base.replace(/\/$/, '');
      return `${trimmed}/${fileName}?t=${this.logoCacheBust}`;
    },
    validateRow() {
      const code = (this.row.code || '').toString();
      if (!code.trim()) {
        this.$alert('Enter Check-in Row Code');
        return false;
      }
      if (code.length > 10) {
        this.$alert('Check-in Row Code cannot be more than 10 characters');
        return false;
      }
      const airlineCode = (this.row.airline_code || '').toString();
      if (airlineCode && airlineCode.length > 5) {
        this.$alert('Airline code cannot be more than 5 characters');
        return false;
      }
      const logoFile = (this.row.logo_file || '').toString();
      if (logoFile && logoFile.length > 255) {
        this.$alert('Logo file name is too long (max 255 characters)');
        return false;
      }
      return true;
    },
    handleApiError(error, defaultMessage) {
      this.loading = false;
      try {
        if (error && error.response) {
          const { status, data } = error.response;
          if (status === 422 && data && data.errors) {
            const messages = [];
            Object.keys(data.errors).forEach((key) => {
              const arr = data.errors[key];
              if (Array.isArray(arr) && arr.length) {
                messages.push(arr[0]);
              }
            });
            if (messages.length) {
              this.$alert(messages.join('\n'));
              return;
            }
          }
          if (data && data.msg) {
            this.$alert(data.msg);
            return;
          }
        }
      } catch (e) {
        // fall through to default message
      }
      this.$alert(defaultMessage || 'Something went wrong while saving Check-in Row.');
    },
    addRow() {
      if (!this.validateRow()) {
        return;
      }
      this.loading = true;
      axios
        .post(this.$hostName + '/checkin-row/store', {
          check_in_row: this.row,
        })
        .then((response) => {
          if (response.status === 202) {
            this.loading = false;
            this.$alert('Check-in Row already exists');
          } else {
            this.row.code = '';
            this.loading = false;
            this.$router.push({ name: routeNames.manageCheckinRow });
          }
        })
        .catch((error) => {
          console.log(error);
          this.handleApiError(error, 'Failed to create Check-in Row.');
        });
    },
    updateRow(id) {
      if (!this.validateRow()) {
        return;
      }
      this.loading = true;
      axios
        .put(this.$hostName + '/checkin-row/' + id, {
          check_in_row: this.row,
          nameCheck: this.nameCheck,
        })
        .then((response) => {
          if (response.status === 202) {
            this.loading = false;
            this.$alert('Check-in Row already exists');
          } else {
            this.loading = false;
            this.$router.push({ name: routeNames.manageCheckinRow });
          }
        })
        .catch((error) => {
          console.log(error);
          this.handleApiError(error, 'Failed to update Check-in Row.');
        });
    },
  },
};
</script>

<style scoped>
.loader {
  position: absolute;
  top: 0px;
  right: 0px;
  width: 100%;
  height: 100%;
  background-color: #eceaea;
  background-image: url('../../assets/loading.gif');
  background-size: 50px;
  background-repeat: no-repeat;
  background-position: center;
  z-index: 10000000;
  opacity: 0.4;
  filter: alpha(opacity=40);
}

.logo-selection {
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 10px 12px;
  background-color: #fafafa;
}

.logo-option {
  cursor: pointer;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  transition: box-shadow 0.15s ease, border-color 0.15s ease, transform 0.1s ease;
}

.logo-option:hover {
  box-shadow: 0 0 6px rgba(0, 0, 0, 0.15);
  border-color: #007bff;
  transform: translateY(-1px);
}

.logo-option.selected {
  border-color: #007bff;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
}

.logo-preview-wrapper {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.checkin-logo-preview {
  max-width: 120px;
  max-height: 60px;
  object-fit: contain;
}

label {
  font-weight: 600;
}
</style>
